import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/button'
import { BackButton } from '../../components/button/backButton'
import Dropdown from '../../components/inputs/dropdown'
import { TextInput } from '../../components/inputs/textInput'
import { SelectCreatorModal } from '../../components/modal/app_modals/selectCreatorModal'
import { PageHeader } from '../../components/typography/PageHeader'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import {
  getPageFromSkip,
  getSkipFromPage,
  parseCurrencyToNumber,
} from '../../helpers'
import { useAppSelector } from '../../hooks'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import {
  useBulkUpdateProductPricesMutation,
  useGetAllProductsQuery,
} from '../../redux-toolkits/products/products.slice'
import { ProductSearchQuery } from '../../redux-toolkits/products/products.type'
import { Product } from '../../redux-toolkits/products/products.type'
import { StrippedVendor } from '../../redux-toolkits/vendors/vendors.types'
import Icon from '../../components/icon'
import { BulkPriceProductPicker } from './components/bulk-price-product-picker'

const priceAdjustmentReasons = [
  { name: 'Inventory re-count', value: 'Inventory re-count' },
  { name: 'Damage', value: 'Damage' },
  { name: 'Loss', value: 'Loss' },
  { name: 'Stock received', value: 'Stock received' },
  { name: 'Others', value: 'Others' },
  { name: 'Restock return', value: 'Restock return' },
  { name: 'Theft', value: 'Theft' },
]

const getProductVendorId = (product: Product) => {
  return product.vendor?._id
}

export const BulkPriceEditPage = () => {
  const navigate = useNavigate()
  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)
  const { handleModalClose, handleModalOpen, modal, modalOpen, setModalOpen } =
    useModalControl()

  const [selectedCreators, setSelectedCreators] = useState<StrippedVendor[]>([])
  const [editedProducts, setEditedProducts] = useState<Record<string, Product>>(
    {},
  )
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({})
  const [adjustmentReason, setAdjustmentReason] = useState<{
    name: string
    value: string
  }>()
  const [query, setQuery] = useState<ProductSearchQuery>({
    skip: 0,
    limit: NUMBER_OF_ITEMS_PER_PAGE,
  })

  const productQuery =
    selectedCreators.length > 0
      ? {
          ...query,
          vendorId: selectedCreators.map((creator) => creator._id).join(','),
          fulfillmentCenterId:
            selectedFulfilmentCenters.map((c) => c._id).join(',') || undefined,
        }
      : skipToken

  const { data: products, isFetching: loadingProducts } =
    useGetAllProductsQuery(productQuery, {
      refetchOnMountOrArgChange: true,
      skip: selectedFulfilmentCenters.length === 0,
    })

  const [bulkUpdateProductPrices, { isLoading: savingPrices }] =
    useBulkUpdateProductPricesMutation()

  const changedProducts = useMemo(() => {
    return Object.values(editedProducts)
      .map((product) => {
        const priceDraft = priceDrafts[product._id]
        const price =
          priceDraft === undefined || priceDraft.trim() === ''
            ? null
            : parseCurrencyToNumber(priceDraft)

        return {
          product,
          price,
        }
      })
      .filter(({ product, price }) => price !== null && price !== product.price)
  }, [editedProducts, priceDrafts])

  const handleCreatorChange = (creators: StrippedVendor[]) => {
    setSelectedCreators(creators)
    setEditedProducts({})
    setPriceDrafts({})
    setAdjustmentReason(undefined)
    setQuery({
      skip: 0,
      limit: NUMBER_OF_ITEMS_PER_PAGE,
    })
  }

  const handlePriceChange = (product: Product, price: string) => {
    setEditedProducts((current) => ({
      ...current,
      [product._id]: product,
    }))
    setPriceDrafts((current) => ({
      ...current,
      [product._id]: price,
    }))
  }

  const handleRemoveCreator = (creatorId: string) => {
    const productIdsToRemove = Object.values(editedProducts)
      .filter((product) => getProductVendorId(product) === creatorId)
      .map((product) => product._id)

    setSelectedCreators((current) =>
      current.filter((creator) => creator._id !== creatorId),
    )

    setEditedProducts((current) => {
      const next = { ...current }
      productIdsToRemove.forEach((productId) => {
        delete next[productId]
      })
      return next
    })

    setPriceDrafts((current) => {
      const next = { ...current }
      productIdsToRemove.forEach((productId) => {
        delete next[productId]
      })
      return next
    })
  }

  const handleSave = async () => {
    if (selectedCreators.length < 1 || !adjustmentReason?.value) {
      return
    }

    const response = await bulkUpdateProductPrices({
      vendorIds: selectedCreators.map((creator) => creator._id),
      adjustmentReason: adjustmentReason.value,
      items: changedProducts.map(({ product, price }) => ({
        productId: product._id,
        price: price ?? product.price,
      })),
    }).unwrap()

    const completedProductIds = response.data
      .filter((item) => item.status !== 'failed')
      .map((item) => item.productId)

    setEditedProducts((current) => {
      const next = { ...current }
      completedProductIds.forEach((productId) => {
        delete next[productId]
      })
      return next
    })

    setPriceDrafts((current) => {
      const next = { ...current }
      completedProductIds.forEach((productId) => {
        delete next[productId]
      })
      return next
    })
  }

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <BackButton />

        <div className="flex flex-wrap items-center justify-between gap-4">
          <PageHeader title="Bulk Edit Product Prices" className="!mb-0" />

          <Button
            type="button"
            label="Back to Products"
            onClick={() => {
              navigate('/products')
            }}
          />
        </div>

        <div className="flex flex-col gap-4 border-b border-divider-gray pb-6">
          <p className="font-medium">Creators</p>

          <div className="flex flex-wrap items-center gap-4">
            <Button
              type="button"
              label={
                selectedCreators.length > 0
                  ? 'Change Creators'
                  : 'Select Creators'
              }
              icon="plus"
              primary={selectedCreators.length < 1}
              onClick={() => {
                handleModalOpen('selectCreatorModal')
              }}
            />

            {selectedCreators.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {selectedCreators.map((creator) => (
                  <div
                    key={creator._id}
                    className="flex items-center gap-3 rounded-md border border-divider-gray px-3 py-2"
                  >
                    <img
                      src={creator.logo}
                      alt={creator.name}
                      className="h-10 w-10 rounded-md object-cover"
                    />
                    <p className="font-medium text-black">{creator.name}</p>
                    <button
                      type="button"
                      aria-label={`Remove ${creator.name}`}
                      title={`Remove ${creator.name}`}
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-divider-gray text-sec-black transition-all duration-300 hover:border-black hover:text-black"
                      onClick={() => {
                        handleRemoveCreator(creator._id)
                      }}
                    >
                      <Icon id="close-icon" width={10} height={10} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-sec-black">
                Select creators to see their products.
              </p>
            )}
          </div>
        </div>

        {selectedCreators.length > 0 ? (
          <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-5">
              <div>
                <p className="font-medium">Creator Products</p>
                <p className="text-sm text-sec-black">
                  Enter new prices only for the products you want to update.
                </p>
              </div>

              <div className="flex flex-col gap-4 rounded-md border border-divider-gray p-4 md:flex-row md:items-end md:justify-between">
                <div className="w-full md:max-w-[360px]">
                  <Dropdown
                    className="w-full"
                    menuButton={
                      <TextInput
                        type="text"
                        name="adjustmentReason"
                        placeholder="Adjustment reason"
                        value={adjustmentReason?.name ?? ''}
                        hasIcon={false}
                        readOnly
                        extra={
                          <Icon
                            id="left-caret"
                            width={16}
                            height={16}
                            className="-rotate-90 text-sec-black"
                          />
                        }
                        className="!border-divider-gray !w-full"
                        title="Adjustment reason"
                      />
                    }
                    menuItems={priceAdjustmentReasons.map((reason) => ({
                      ...reason,
                      onClick: () => {
                        setAdjustmentReason(reason)
                      },
                    }))}
                  />
                </div>

                <Button
                  type="button"
                  label="Save Price Changes"
                  primary
                  loading={savingPrices}
                  disabled={
                    savingPrices ||
                    changedProducts.length < 1 ||
                    !adjustmentReason?.value
                  }
                  onClick={handleSave}
                  className="w-full md:w-fit"
                />
              </div>

              <BulkPriceProductPicker
                products={products?.data.data ?? []}
                priceDrafts={priceDrafts}
                loading={loadingProducts}
                search={query.search}
                currentPage={Number(
                  getPageFromSkip(query.skip ?? 0, query.limit ?? 20),
                )}
                totalDataCount={products?.data.totalItems ?? 0}
                pageLimit={Number(query.limit ?? NUMBER_OF_ITEMS_PER_PAGE)}
                onSearch={(search) => {
                  setQuery((current) => ({
                    ...current,
                    search: search || undefined,
                    skip: 0,
                  }))
                }}
                onPageChange={(page) => {
                  setQuery((current) => ({
                    ...current,
                    skip: getSkipFromPage(page, current.limit ?? 20),
                  }))
                }}
                onPriceChange={handlePriceChange}
              />
            </section>
          </div>
        ) : undefined}

        <SelectCreatorModal
          open={modalOpen && modal === 'selectCreatorModal'}
          setOpen={setModalOpen}
          selectedCreators={selectedCreators}
          onCloseCallback={() => {
            handleModalClose()
          }}
          onSuccessCallback={(creators) => {
            handleCreatorChange(creators)
            handleModalClose()
          }}
        />
      </div>
    </Layout>
  )
}
